import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { createSegment, fetchSegmentPreview, fetchSegments } from '@/data/sources-marketinga';
import type {
  Segment,
  SegmentContact,
  SegmentFilter,
  SegmentFilterOp,
  SegmentPreview,
} from '@/types/marketinga';
import { formatRelative } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

/* /marketing/segments — Segments (W3b). Build filters + preview count/contacts.
 *
 * HONESTY + SAFETY INVARIANTS (W3b contract):
 *  - The segment list is a FREE read (auto-loads). Creating a segment is an
 *    explicit write (owner/operator). Preview is a FREE, read-only count over
 *    EXISTING contacts — no send, no charge.
 *  - Preview count is a real integer: an empty segment previews as a genuine 0
 *    with an honest empty state, never a fabricated audience.
 *  - Source-badged ("bc_contacts+filters"); the preview restates it read-only.
 */

const SOURCE_TAG = 'Source: Segments (bc_contacts+filters)';

const OP_LABEL: Record<SegmentFilterOp, string> = {
  eq: 'is',
  neq: 'is not',
  contains: 'contains',
  exists: 'exists',
  not_exists: 'is empty',
};

/** Ops that ignore the value input (presence checks). */
const VALUELESS_OPS: SegmentFilterOp[] = ['exists', 'not_exists'];

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'Segments are limited to workspace owners and operators.';
    if (err.status === 422) return err.message || 'Please check the segment filters and try again.';
    return err.message || 'The data service returned an unexpected response.';
  }
  return err instanceof Error ? err.message : 'Unexpected error. Try again.';
}

function filterSummary(f: SegmentFilter): string {
  if (VALUELESS_OPS.includes(f.op)) return `${f.field || '(field)'} ${OP_LABEL[f.op]}`;
  return `${f.field || '(field)'} ${OP_LABEL[f.op]} "${f.value}"`;
}

const inputClass =
  'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

const OP_OPTIONS: SegmentFilterOp[] = ['eq', 'neq', 'contains', 'exists', 'not_exists'];

/* -------------------------------------------------------------------------- */
/* Filter builder + create form.                                               */
function SegmentBuilder({
  disabled,
  onCreated,
}: {
  disabled: boolean;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [filters, setFilters] = useState<SegmentFilter[]>([{ field: '', op: 'eq', value: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setFilter = (i: number, patch: Partial<SegmentFilter>) =>
    setFilters((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const addFilter = () => setFilters((prev) => [...prev, { field: '', op: 'eq', value: '' }]);
  const removeFilter = (i: number) =>
    setFilters((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  // Only clauses with a field are meaningful; presence ops need no value.
  const validFilters = filters.filter(
    (f) => f.field.trim() !== '' && (VALUELESS_OPS.includes(f.op) || f.value.trim() !== ''),
  );
  const canSubmit = !disabled && !busy && name.trim() !== '' && validFilters.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await createSegment({
        name: name.trim(),
        filters: validFilters.map((f) => ({
          field: f.field.trim(),
          op: f.op,
          value: VALUELESS_OPS.includes(f.op) ? '' : f.value.trim(),
        })),
      });
      setName('');
      setFilters([{ field: '', op: 'eq', value: '' }]);
      onCreated();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="engine">New segment</Badge>
        <span className="text-sm font-medium text-text-primary">Build a segment</span>
      </div>
      <p className="mt-1 text-sm text-text-secondary">
        Combine filters over your existing contacts. Saving a segment reads nothing and sends
        nothing — preview the resulting contacts from the list below.
      </p>

      {disabled && (
        <div role="status" className="mt-3 rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          Building segments is limited to workspace owners and operators.
        </div>
      )}

      <form
        className="mt-3 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Segment name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Wales studios — no reply yet"
            aria-label="Segment name"
            className={`${inputClass} max-w-md`}
            disabled={disabled || busy}
          />
        </label>

        <div className="space-y-2">
          <span className="text-xs font-medium text-text-secondary">Filters</span>
          {filters.map((f, i) => {
            const valueless = VALUELESS_OPS.includes(f.op);
            return (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={f.field}
                  onChange={(e) => setFilter(i, { field: e.target.value })}
                  placeholder="field (e.g. region)"
                  aria-label={`Filter ${i + 1} field`}
                  className={`${inputClass} min-w-[10rem] flex-1`}
                  disabled={disabled || busy}
                />
                <select
                  value={f.op}
                  onChange={(e) => setFilter(i, { op: e.target.value as SegmentFilterOp })}
                  aria-label={`Filter ${i + 1} operator`}
                  className={inputClass}
                  disabled={disabled || busy}
                >
                  {OP_OPTIONS.map((op) => (
                    <option key={op} value={op}>
                      {OP_LABEL[op]}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={valueless ? '' : f.value}
                  onChange={(e) => setFilter(i, { value: e.target.value })}
                  placeholder={valueless ? 'no value needed' : 'value'}
                  aria-label={`Filter ${i + 1} value`}
                  className={`${inputClass} min-w-[10rem] flex-1`}
                  disabled={disabled || busy || valueless}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeFilter(i)}
                  disabled={disabled || busy || filters.length <= 1}
                  title="Remove filter"
                  aria-label={`Remove filter ${i + 1}`}
                >
                  ✕
                </Button>
              </div>
            );
          })}
          <Button type="button" variant="secondary" onClick={addFilter} disabled={disabled || busy}>
            + Add filter
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {busy ? 'Saving…' : 'Create segment'}
          </Button>
          {error && <span className="text-sm text-danger">{error}</span>}
        </div>
      </form>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Preview panel — free read-only count + contacts for a saved segment.        */
type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'done'; data: SegmentPreview };

const CONTACT_COLUMNS: SortableColumn<SegmentContact>[] = [
  {
    key: 'name',
    header: 'Contact',
    sortable: true,
    sortValue: (c) => c.name,
    render: (c) => c.name,
    truncate: true,
    maxWidthClass: 'max-w-[220px]',
    title: (c) => c.name,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'email',
    header: 'Email',
    render: (c) => (c.email ? c.email : <span className="text-text-tertiary">—</span>),
    truncate: true,
    maxWidthClass: 'max-w-[240px]',
    title: (c) => c.email || undefined,
  },
  {
    key: 'detail',
    header: 'Detail',
    render: (c) => (c.detail ? c.detail : <span className="text-text-tertiary">—</span>),
    truncate: true,
    maxWidthClass: 'max-w-[240px]',
    title: (c) => c.detail || undefined,
  },
];

function PreviewPanel({ segment, state }: { segment: Segment; state: PreviewState }) {
  return (
    <div className="mt-3 rounded-md border border-border-subtle bg-surface p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="neutral">Preview · {segment.name}</Badge>
        <span className="text-xs text-text-tertiary">read-only over existing contacts</span>
      </div>
      <div className="mt-3">
        {state.status === 'idle' && (
          <p className="text-sm text-text-tertiary">Select “Preview” on a segment to see who it matches.</p>
        )}
        {state.status === 'loading' && <LoadingState rows={4} />}
        {state.status === 'error' && (
          <div role="alert" className="rounded-md border border-warning/30 bg-canvas px-3 py-2 text-sm text-text-secondary">
            {state.message}
          </div>
        )}
        {state.status === 'done' &&
          (state.data.count === 0 ? (
            <EmptyState
              title="No contacts match yet"
              teach="These filters match zero contacts in your workspace right now — an honest empty preview, not an error. Widen the filters or import more contacts and preview again."
            />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
              <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle px-3 py-2">
                <span className="font-mono text-lg font-semibold text-text-primary" data-tabular>
                  {formatInt(state.data.count)}
                </span>
                <span className="text-xs text-text-tertiary">
                  {state.data.count === 1 ? 'contact matches' : 'contacts match'} · showing the first{' '}
                  {formatInt(state.data.contacts.length)}
                </span>
              </div>
              <SortableTable
                rows={state.data.contacts}
                columns={CONTACT_COLUMNS}
                getRowKey={(c, i) => `${c.id}-${i}`}
                minWidthClass="min-w-[640px]"
                caption="Contacts matched by the segment filters"
              />
              <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                Read-only from {state.data.source} · previewing never sends and never charges.
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

/* ========================================================================== */
export function SegmentsPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();

  const segmentsQ = useQuery({ queryKey: ['marketing', 'segments'], queryFn: fetchSegments });
  const segments = segmentsQ.data?.segments ?? [];
  const source = segmentsQ.data?.source ?? 'bc_contacts+filters';

  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });

  const runPreview = async (segment: Segment) => {
    setPreviewFor(segment.id);
    setPreview({ status: 'loading' });
    try {
      const data = await fetchSegmentPreview(segment.id);
      setPreview({ status: 'done', data });
    } catch (err) {
      setPreview({ status: 'error', message: describeError(err) });
    }
  };

  const columns: SortableColumn<Segment>[] = [
    {
      key: 'name',
      header: 'Segment',
      sortable: true,
      sortValue: (s) => s.name,
      render: (s) => <span className="text-sm font-medium text-text-primary">{s.name}</span>,
    },
    {
      key: 'filters',
      header: 'Filters',
      render: (s) => (
        <div className="flex flex-wrap gap-1">
          {s.filters.length === 0 ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            s.filters.map((f, i) => (
              <Badge key={i} tone="neutral">
                {filterSummary(f)}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      key: 'count',
      header: 'Last count',
      align: 'right',
      numeric: true,
      sortable: true,
      // Null (never previewed) sorts LAST via the table's null-honest rule.
      sortValue: (s) => s.count,
      render: (s) =>
        s.count === null ? (
          <span className="text-xs italic text-text-tertiary">Not previewed yet</span>
        ) : (
          <span className="font-mono text-sm text-text-primary" data-tabular>
            {formatInt(s.count)}
          </span>
        ),
    },
    {
      key: 'created',
      header: 'Created',
      align: 'right',
      render: (s) => (
        <span className="text-xs text-text-tertiary" data-tabular>
          {s.created_at ? formatRelative(s.created_at) : '—'}
        </span>
      ),
    },
    {
      key: 'preview',
      header: 'Preview',
      align: 'right',
      render: (s) => (
        <Button
          variant="secondary"
          onClick={() => void runPreview(s)}
          disabled={previewFor === s.id && preview.status === 'loading'}
          title="Preview matching contacts — read-only"
        >
          {previewFor === s.id && preview.status === 'loading' ? 'Previewing…' : 'Preview'}
        </Button>
      ),
    },
  ];

  const activeSegment = segments.find((s) => s.id === previewFor) ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Segments"
        subtitle="Build audiences from filters over your existing contacts, then preview exactly who matches — a read-only count, no send and no charge."
      />

      <SegmentBuilder
        disabled={!canManage}
        onCreated={() => void queryClient.invalidateQueries({ queryKey: ['marketing', 'segments'] })}
      />

      {segmentsQ.isPending ? (
        <LoadingState rows={5} />
      ) : segmentsQ.error ? (
        <ErrorState error={segmentsQ.error} onRetry={() => void segmentsQ.refetch()} />
      ) : (
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{SOURCE_TAG}</Badge>
            <span className="text-xs text-text-tertiary">
              {segments.length
                ? `${formatInt(segments.length)} ${segments.length === 1 ? 'segment' : 'segments'}`
                : 'No segments yet'}
            </span>
          </div>

          <div className="mt-4">
            {segments.length === 0 ? (
              <EmptyState
                title="No segments yet"
                teach="Build your first segment above by combining filters over your contacts. Once saved, preview it here to see the exact contacts it matches — nothing is sent and nothing is charged."
              />
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                <SortableTable
                  rows={segments}
                  columns={columns}
                  getRowKey={(s) => s.id}
                  minWidthClass="min-w-[860px]"
                  caption="Segments with their filters, last previewed count and a preview action"
                />
                <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                  {formatInt(segments.length)} {segments.length === 1 ? 'segment' : 'segments'} · read
                  from {source} · previewing is read-only over existing contacts.
                </div>
              </div>
            )}
          </div>

          {activeSegment && <PreviewPanel segment={activeSegment} state={preview} />}
        </Card>
      )}
    </div>
  );
}
